import NoDataTableSvg from '@/assets/svg/NoDataTableSvg';
import OutfallIconSvg from '@/assets/svg/OutfallIconSvg';
import ParameterIconTableSvg from '@/assets/svg/ParameterIconTableSvg';
import PluviometerIconSvg from '@/assets/svg/PluviometerIconSvg';
import { LegendTable } from '@/components/Legend/LegendTable';
import DashboardContext from '@/contexts/DashboardContext';
import InfrastructureService from '@/domain/Infrastructure/InfraestructureService';
import MroService from '@/domain/Mro/MroService';
import { getReadingsFromDevice } from '@/services/historical';
import { getResumeFromDevice } from '@/services/measurement';
import { infraTypes } from '@/statics/constants/infraTypes';
import { generateUsageColor } from '@/utils/generateUsageColor';
import { SearchOutlined } from '@ant-design/icons';
import {
	Accordion,
	Button,
	Checkbox,
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxList,
	DataGrid,
	DataGridColumn,
	Icon,
	Input,
	Tooltip,
} from '@hydrodigital/design-system';
import { Spin } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts/highstock';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import './index.scss';
dayjs.extend(utc);

const infrastructureService = new InfrastructureService();
const mroService = new MroService();

export default function ReportsMroView() {
	const { value: context } = useContext(DashboardContext);
	const chartRef2 = useRef<any>(null);
	const [graphOptions, setGraphOptions] = useState({});
	const [parameters, setParameters] = useState([]);
	const [selectedSensors, setSelectedSensors] = useState<any>([]);
	const [isDataAvailable, setIsDataAvailable] = useState(false);
	const [dataModalDetail, setDataModalDetail] = useState<any[]>([]);
	const [pagination, setPagination] = useState({
		page: 1,
		pageSize: 10,
		totalRecords: 0,
		currentPage: 1,
	});
	const [queryParams, setQueryParams] = useState('');
	const [invisibleParams, setInvisibleParams] = useState<any[]>([]);

	const [outfallList, setOutfallList] = useState<any[]>([]);
	const [pluviometerList, setPluviometerList] = useState<any[]>([]);
	const [selectedOutfall, setSelectedOutfall] = useState<any>(null);
	const [selectedPluviometer, setSelectedPluviometer] = useState<any>(null);
	const [highchartVmRef, setHighchartVmRef] = useState<any>(null);
	const allReadings = useRef<any[]>([]);
	const allResumes = useRef<any[]>([]);
	const [isSpinnerReadingVisible, setIsSpinnerReadingVisible] = useState(false);

	const legendInfo = [
		{ label: 'Less than 0%', color: '#22a2ca' },
		{ label: '0% - 33%', color: '#34c38f' },
		{ label: '33% - 66%', color: '#ffd992' },
		{ label: '66% - 100%', color: '#e3a63d' },
		{ label: 'Greater than 100%', color: '#f46a6a' },
	];
	const columnsExpanded = useMemo(
		(): DataGridColumn<any>[] => [
			{
				header: () => { return <Icon children={<ParameterIconTableSvg />} />},
				id: 'A',
				cell: ({row, getValue}: any) => <ParameterIconTableSvg />,
				width: 100,
			},
			{
				header: 'infrastructureName',
				accessorKey: 'infrastructureName',
				cell: ({getValue}) => <span>{getValue()}</span>,
				id: 'infrastructureName',
				width: 200,
			},
			{
				header: 'Parameter',
				accessorKey: 'parameter',
				cell: ({getValue}) => <span>{getValue()}</span>,
				id: 'parameter',
				width: 200,
			},
			{
				header: 'Type',
				accessorKey: 'type',
				cell: ({getValue}) => <span>{getValue()}</span>,
				id: 'type',
				width: 80,
			},
			{
				header: 'Source',
				cell: ({row, getValue}) => (
					<div className="flex column">
						<span>{`${row.original.sourceName}`}</span>
					</div>
				),
				width: 160,
			},
			{
				header: 'Units',
				accessorKey: 'unit',
				cell: ({getValue}) => <span>{getValue()}</span>,
				id: 'unit',
				width: 80,
			},
			{
				header: 'Last Value',
				cell: ({row, getValue}) => (
					<>
						<span>{`${row.original.currentValue}`}</span>
					</>
				),
				width: 90,
			},
			{
				header: 'Last Collected',
				cell: (
					{row,
					getValue}
				) => (
					<span>
						{row.original.lastCollected && row.original.lastCollected != 'N/A'
							? `${dayjs(row.original.lastCollected).format('YYYY/MM/DD hh:mm A')}`
							: 'No data available'}
					</span>
				),
				width: 180,
			},
			{
				header: 'Capacity',
				cell: ({
					row: {
						original:{
							usage,
							color,
							currentValue,
							criticalLow,
							criticalHigh,
						}
					},
					getValue}
				) => (
					<div className="flex items-center gap-2 overflow-hidden">
						{ usage !== null ? <div
							className="w-3 h-3 rounded-full"
							style={{ backgroundColor: generateUsageColor(usage) }}
							/> : null}
						<Tooltip title={usage !== null ? `${usage > 0 ? usage.toFixed(2) : usage} %` : 'No data'}>
							<span className="overflow-hidden whitespace-nowrap text-ellipsis w-20">{usage !== null ? `${usage > 0 ? usage.toFixed(2) : usage} %` : 'No data'}</span>
						</Tooltip>
					</div>
				),
				width: 100,
			},
			{
				header: 'Max.',
				cell: ({row, getValue}) => <span>{`${row.original.maximun}`}</span>,
				width: 90,
			},
			{
				header: 'Min.',
				cell: ({row, getValue}) => <span>{`${row.original.minimum}`}</span>,
				width: 90,
			},
			{
				header: 'Sum',
				cell: (
					{row: {
						original:{
							source,
							sum
						}
					},
					getValue}
				) => <span>{source ? sum : '-'}</span>,
				width: 100,
			},
			{
				header: 'Average',
				cell: ({row, getValue}) => <span>{`${row.original.average}`}</span>,
				width: 90,
			},
			{
				header: 'Critical High High',
				cell: ({row, getValue}) => (
					<span>{`${row.original.criticalHhigh}`}</span>
				),
				width: 90,
			},
			{
				header: 'Critical High',
				cell: ({row, getValue}) => <span>{`${row.original.criticalHigh}`}</span>,
				width: 90,
			},
			{
				header: 'Critical Low',
				cell: ({row, getValue}) => <span>{`${row.original.criticalLow}`}</span>,
				width: 90,
			},
			{
				header: 'Critical Low Low',
				cell: ({row, getValue}) => <span>{`${row.original.criticalLlow}`}</span>,
				width: 90,
			},
		],
		[],
	);

	const handleGetPluviometers = async () => {
		const locationId = context.selectedLocation || localStorage.getItem('Location') || '';
		const infra: any = await infrastructureService.getInfraestructureInUse(
			locationId,
			1,
			10000,
			'',
			'',
			`${infraTypes.PLUVIOMETER}`,
			null,
		);
		if (infra) {
			setPluviometerList(infra.data);
		}
	};

	const handleGetOutfall = async () => {
		const locationId = context.selectedLocation || localStorage.getItem('Location') || '';
		const infra: any = await infrastructureService.getInfraestructureInUse(
			locationId,
			1,
			10000,
			'',
			'',
			`${infraTypes.OUTFALL}`,
			null,
		);
		if (infra) {
			setOutfallList(infra.data);
		}
	};

	async function fetchDataToGraph() {
		const [dateFrom, dataTo] = context.selectedDate;
		if (!dateFrom || !dataTo) return;
		Promise.all(
			selectedSensors.map(async (sl: any) => {
				const sensor = sl.split('+');
				const rangeDates: any = compareDates(dataTo, dateFrom);
				return Promise.all([
					getReadingsFromDevice(
						sensor[1],
						sensor[2],
						dayjs(dateFrom).format('YYYY-MM-DD HH:mm:ss'),
						dayjs(dataTo).format('YYYY-MM-DD HH:mm:ss'),
						rangeDates[0],
						rangeDates[1],
					),
					getResumeFromDevice(
						sensor[0],
						sensor[1],
						sensor[2],
						dayjs(dateFrom).format('YYYY-MM-DD HH:mm:ss'),
						dayjs(dataTo).format('YYYY-MM-DD HH:mm:ss'),
					),
				]).then(([reading, resume]) => {
					allReadings.current.push({
						...reading,
						resume,
						sensorString: sl,
						serialNumberMeasurementTool: sensor[2],
						paramId: sensor[1],
						source: sensor[3],
					});
					allResumes.current.push({
						...resume,
						sensorString: sl,
						serialNumberMeasurementTool: sensor[2],
						paramId: sensor[1],
						source: sensor[3],
					});
					return {
						...reading,
						resume,
						serialNumberMeasurementTool: sensor[2],
						paramId: sensor[1],
						source: sensor[3],
					};
				});
			}),
		).then((dataArray) => {
			const data = dataArray
				.filter((item) => item !== undefined)
				.filter((sl: any) => {
					const selectedPluv: any = pluviometerList?.find(
						(pluviometer) => pluviometer.infraestructureId == selectedPluviometer,
					);

					const selectedOut: any = outfallList?.find(
						(outfall) => outfall.infraestructureId == selectedOutfall,
					);

					return (
						(selectedPluv && sl.infrastructureId == selectedPluv.infraestructureId) ||
						(selectedOut && sl.infrastructureId == selectedOut.infraestructureId)
					);
				});
			buildDataToGraph(data);
			buildDataToTable(data.map((item) => item.resume));
			setIsSpinnerReadingVisible(false);
		});
	}

	function compareDates(date1: any, date2: any) {
		const startDate = dayjs(date2);
		const endDate = dayjs(date1);
		const diffInMonths = endDate.diff(startDate, 'month', true); // true para obtener fracción de mes

		if (diffInMonths < 1) {
			return ['5', 'minute'];
		} else if (diffInMonths >= 1 && diffInMonths <= 6) {
			return ['15', 'minute'];
		} else {
			return ['1', 'hour'];
		}
	}

	function buildDataToGraph(dataArray: any) {
		let yAxis: {
			valueUnit: any;
			labels: { format: string };
			title: { text: string };
		}[] = [];
		dataArray.forEach((item: { valueUnit: any; sensorType: any } | undefined) => {
			if (item === undefined) return;
			if (!yAxis.find(({ valueUnit }) => valueUnit === item.valueUnit)) {
				yAxis.push({
					valueUnit: item.valueUnit,
					labels: {
						format: `{value}${item.valueUnit}`,
					},
					title: {
						text: `${item.sensorType}`,
					},
				});
			}
		});
		let seriesData: any[] = [];
		dataArray.forEach((data: any, index: string | number) => {
			if (data === undefined) return;
			const measurementHistorialValues = data.measurementToolHistoricalValues;
			if (measurementHistorialValues) {
				const keys = Object.keys(measurementHistorialValues);
				const dataSeries = keys
					.map((item) => {
						return {
							x: parseFloat(item),
							y: measurementHistorialValues[item].v,
							origen: measurementHistorialValues[item].s,
						};
					})
					.filter((item) => {
						return item.y != null;
					})
					.sort((a, b) => {
						return a.x - b.x;
					});

				const read = {
					name: data.measurementTargetName,
					paramId: data.paramId,
					valueUnit: data.valueUnit,
					sensorType: data.sensorType,
					yAxis: yAxis.findIndex(({ valueUnit }) => valueUnit === data.valueUnit),
					data: dataSeries,
				};
				seriesData.push(read);
			}
			if (data.measurementToolForecastValues) {
				data.measurementToolForecastValues?.forEach((forecast: { [x: string]: any }) => {
					const colors: any = Highcharts.getOptions().colors;
					if (!colors) return;
					const foreData = {
						name: data.measurementTargetName,
						paramId: data.paramId,
						sourceName: data.resume.sourceName,
						valueUnit: data.valueUnit,
						sensorType: data.sensorType,
						yAxis: yAxis.findIndex(({ valueUnit }) => valueUnit === data.valueUnit),
						showInLegend: false,
						color: colors[index],
						dashStyle: 'Dash',
						data: forecast
							? Object.keys(forecast)
									.map((item) => {
										return {
											x: parseFloat(item),
											y: forecast[item],
											origen: data.resume.sourceName,
										};
									})
									.filter((item) => {
										return item.y != null;
									})
									.sort((a, b) => {
										return a.x - b.x;
									})
							: [],
					};
					seriesData.push(foreData);
				});
			}
		});

		if (seriesData.length <= 0) {
			setIsDataAvailable(false);
		} else {
			setIsDataAvailable(true);
		}

		const [dateFrom, dataTo] = context.selectedDate;
		const localOffset = new Date().getTimezoneOffset() * 60 * 1000;
		setGraphOptions({
			title: {
				text: '',
			},
			credits: {
				enabled: false,
			},
			chart: {
				type: 'line',
				zoomType: 'x',
				panKey: 'shift',
				height: null,
				plotBorderWidth: 1,
				backgroundColor: '#FFFFFF',
				events: {
					load: function () {
						setHighchartVmRef(this);
					},
					render() {
						setHighchartVmRef(this);
						chartRef2.current = this;
					},
				},
			},
			legend: {
				backgroundColor: '#FFFFFF',
				enabled: false,
			},
				xAxis: {
					type: 'datetime',
					dateTimeLabelFormats: {
						millisecond: '%l:%M:%S%p',
						second: '%l:%M:%S%p',
						minute: '%l:%M%p',
						hour: '%l:%M%p',
						day: '%e. %b',
						week: '%e. %b',
						month: "%b '%y",
						year: '%Y',
					},
					plotBands: [
						{
							from: dayjs().valueOf() - localOffset,
						to: dayjs('2090-01-01 00:00:00'),
						color: '#EFFFFF',
					},
				],
				min: new Date(dateFrom).getTime() - localOffset,
				max: new Date(dataTo).getTime() - localOffset,
				ordinal: false,
			},
			yAxis: yAxis,
			plotOptions: {
				series: {
					label: {
						connectorAllowed: false,
					},
					turboThreshold: 0,
				},
			},
			navigator: {
				enabled: true,
				adaptToUpdatedData: true,
				stickToMax: false,
			},
			tooltip: {
				formatter: function (): any {
					const vm: any = this;
					let text = `<span>${dayjs(vm.points[0].x + localOffset).format(
						'YYYY-MM-DD hh:mm:ss A',
					)}</span><br/><br/>`;
					vm.points.forEach((point: any) => {
						const series = point.series;
						const symbol = '●';
						text += `<span style="color:${series.color}">${symbol} ${series.name}</span>: <b>${point.y} ${series.userOptions.valueUnit}</b><br/><span> Source: <b>${point.point.origen}</b></span><br/>`;
					});
					return [text];
				},
				valueDecimals: 2,
				shared: true,
			},
			rangeSelector: {
				buttonTheme: {
					style: {
						display: 'none',
					},
				},
				enabled: false,
				dropdown: 'always',
				inputDateFormat: '%Y/%m/%d %I:%M %p',
				selected: 1,
			},
			series: seriesData,
		});
		if (highchartVmRef && highchartVmRef.xAxis) {
			highchartVmRef.xAxis[0] && highchartVmRef.xAxis[0].setExtremes(null, null);
			highchartVmRef.xAxis[1] && highchartVmRef.xAxis[1].setExtremes(null, null);
		}
	}

	function buildDataToTable(dataArray: any[]) {
		const seriesData = dataArray.map((resume, index: string | number) => {
			if (resume === undefined) return {};
			const colors: any = Highcharts.getOptions().colors;
			if (!colors) return {};
			if (resume) {
				return {
					parameter: resume.parameterName,
					type: resume.sourceType,
					unit: resume.unit,
					criticalHigh: resume.criticalHigh,
					criticalHhigh: resume.criticalHhigh,
					criticalLow: resume.criticalLow,
					criticalLlow: resume.criticalLlow,
					currentValue: resume.currentValue,
					lastCollected: resume.lastCollected,
					usage: resume.usage,
					sourceName: resume.sourceName,
					maximun: resume.max,
					minimum: resume.min,
					average: resume.average,
					color: getColor(
						resume.currentValue,
						resume.criticalHigh,
						resume.criticalHhigh,
						resume.criticalLow,
						resume.criticalLlow,
					),
					colorLine: colors[index],
					measurementTargetId: resume.measurementTargetId,
					sum: resume.sum,
					source: resume.source,
					infrastructureName: resume.infrastructureName,
				};
			}
		});
		setDataModalDetail(seriesData.filter((series) => series));
	}

	function getColor(
		currentValue: number,
		criticalHigh: number,
		criticalHhigh: number,
		criticalLow: number,
		criticalLlow: number,
	) {
		if (criticalHigh >= currentValue && currentValue <= criticalHhigh) {
			return 'orange';
		}
		if (criticalHhigh <= currentValue) {
			return 'red';
		}
		if (criticalLow >= currentValue && currentValue <= criticalLlow) {
			return 'orange';
		}
		if (currentValue <= criticalLlow) {
			return 'red';
		}
		if (criticalLow <= currentValue && currentValue <= criticalHigh) {
			return 'green';
		}
		return 'green';
	}

	async function handleGetMroReport() {
		const [startDate, endDate] = context.selectedDate;
		const infrastructure = selectedSensors.map((sl: any) => {
			const sensor = sl.split('+');
			return {
				infrastructureId: sensor[0],
				paramId: sensor[1],
				sourceId: sensor[3],
			};
		});
		// const outfall = selectedSensors.map((sensorString) => {
		//   const sensor = JSON.parse(sensorString);
		//   return {
		//     infrastructureId: sensor.infrastructureId,
		//     paramId: sensor.measurementTargetId,
		//     sourceId: sensor.sourceId,
		//   };
		// });
		const response = await mroService.getMROReport({
			startDate: dayjs(startDate).format('YYYY-MM-DD HH:mm:ss'),
			endDate: dayjs(endDate).format('YYYY-MM-DD HH:mm:ss'),
			infrastructure,
		});
		base64ToFile(response, 'MRO-Report.xlsx');
	}

	function base64ToFile(base64: string, fileName: string) {
		var binaryData = atob(base64);

		var byteArray = new Uint8Array(binaryData.length);
		for (var i = 0; i < binaryData.length; i++) {
			byteArray[i] = binaryData.charCodeAt(i);
		}

		var blob = new Blob([byteArray], {
			type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		});

		var link = document.createElement('a');
		link.href = window.URL.createObjectURL(blob);
		link.download = fileName;

		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}

	function generateSensorString({ infra, param, source }) {
		return `${infra.infraestructureId}+${param.paremeterId}+${source.serialNumber}+${source.sourceId}`;
	}

	async function checkSensor(sensorString: string) {
		setIsSpinnerReadingVisible(true); // ⬅️ enciende loader

		try {
			// 1️⃣  agrega el sensor al estado
			setSelectedSensors((prev: any[]) => [...prev, sensorString]);

			const [dateFrom, dateTo] = context.selectedDate;
			if (!dateFrom || !dateTo) return; // early-return sigue dentro del try

			const [infraId, paramId, serial, sourceId] = sensorString.split('+');
			const [step, unit] = compareDates(dateTo, dateFrom);

			// 2️⃣  espera las dos peticiones en paralelo
			const [reading, resume] = await Promise.all([
				getReadingsFromDevice(
					paramId,
					serial,
					dayjs(dateFrom).format('YYYY-MM-DD HH:mm:ss'),
					dayjs(dateTo).format('YYYY-MM-DD HH:mm:ss'),
					step,
					unit,
				),
				getResumeFromDevice(
					infraId,
					paramId,
					serial,
					dayjs(dateFrom).format('YYYY-MM-DD HH:mm:ss'),
					dayjs(dateTo).format('YYYY-MM-DD HH:mm:ss'),
				),
			]);

			// 3️⃣  guarda resultados y vuelve a dibujar
			allReadings.current.push({
				...reading,
				resume,
				sensorString,
				serialNumberMeasurementTool: serial,
				paramId,
				source: sourceId,
			});

			allResumes.current.push({
				...resume,
				sensorString,
				serialNumberMeasurementTool: serial,
				paramId,
				source: sourceId,
			});

			renderSensors();
		} finally {
			setIsSpinnerReadingVisible(false); // ⬅️ apaga loader siempre
		}
	}

	function uncheckSensor(sensorString: string) {
		setIsSpinnerReadingVisible(true);
		setTimeout(() => {
			setSelectedSensors((sl: any) => {
				return sl.filter((item: any) => item != sensorString);
			});
			allReadings.current = allReadings.current.filter((item) => {
				return item.sensorString != sensorString;
			});
			allResumes.current = allResumes.current.filter((item) => {
				return item.sensorString != sensorString;
			});
			renderSensors();
			setIsSpinnerReadingVisible(false);
		}, 1000);
	}

	function renderSensors() {
		const currentReadings = allReadings.current;
		// .filter((item) => {
		// 	return (
		// 		item.infrastructureId == selectedPluviometer || item.infrastructureId == selectedOutfall
		// 	);
		// });
		const currentResumes = allResumes.current;
		// .filter((item) => {
		// 	return (
		// 		item.infrastructureId == selectedPluviometer || item.infrastructureId == selectedOutfall
		// 	);
		// });
		buildDataToGraph(currentReadings);
		buildDataToTable(currentResumes);
	}

	async function fetchAllCurrentSensors() {}

	const paramsToShowInTable = useCallback(() => {
		if (queryParams != '') {
			return dataModalDetail.filter((md) => {
				return md.parameter.includes(queryParams);
			});
		} else {
			return dataModalDetail;
		}
	}, [queryParams, dataModalDetail]);

	const renderPluviometers = useCallback(() => {
		const selected: any = pluviometerList?.find(
			(pluviometer) => pluviometer.infraestructureId == selectedPluviometer,
		);
		if (selectedPluviometer) {
			return (
				<div className="flex column pt-2">
					{selected.measurementParameters.map((param: any) => {
						return (
							<div className="flex column" key={param.paremeterId}>
								<div className="flex column mb-2">
									<span className="font-bold mb-2">{param.paremeterName}</span>
									<Checkbox
										className='mb-1'
										size='small'
										checked={
											selectedSensors.indexOf(
												`${selected.infraestructureId}+${param.paremeterId}+null+null`,
											) >= 0
										}
										onChecked={(val) => {
											const sensorString = generateSensorString({
												infra: selected,
												param,
												source: { serialNumber: null, sourceId: null },
											});
											if (val) {
												checkSensor(sensorString);
											} else {
												uncheckSensor(sensorString);
											}
										}}
										label={'Recommended value'}
									/>
									{param.sources.map((source: any) => {
										return (
											<Checkbox
												className='mb-1'
												size='small'
												checked={
													selectedSensors.indexOf(
														`${selected.infraestructureId}+${param.paremeterId}+${source.serialNumber}+${source.sourceId}`,
													) >= 0
												}
												onChecked={(val) => {
													const sensorString = generateSensorString({
														infra: selected,
														param,
														source,
													});
													if (val) {
														checkSensor(sensorString);
													} else {
														uncheckSensor(sensorString);
													}
												}}
												label={source.sourceName}
											/>
										);
									})}
								</div>
							</div>
						);
					})}
				</div>
			);
		}
		return null;
	}, [selectedPluviometer, selectedSensors]);

	const renderOutfalls = useCallback(() => {
		const selected: any = outfallList?.find(
			(outfall) => outfall.infraestructureId == selectedOutfall,
		);
		if (selected) {
			return (
				<div className="flex column pt-2">
					{selected.measurementParameters.map((param: any) => {
						return (
							<div className="flex column" key={param.paremeterId}>
								<div className="flex column mb-2">
									<span className="font-bold mb-2">{param.paremeterName}</span>
									<Checkbox
										size='small'
										checked={
											selectedSensors.indexOf(
												`${selected.infraestructureId}+${param.paremeterId}+null+null`,
											) >= 0
										}
										onChecked={(val) => {
											const sensorString = generateSensorString({
												infra: selected,
												param,
												source: { serialNumber: null, sourceId: null },
											});
											if (val) {
												checkSensor(sensorString);
											} else {
												uncheckSensor(sensorString);
											}
										}}
										className='mb-1'
										label={'Recommended value'}
									/>
									{param.sources.map((source: any) => {
										return (
											<Checkbox
											    className='mb-1'
												size='small'
												checked={
													selectedSensors.indexOf(
														`${selected.infraestructureId}+${param.paremeterId}+${source.serialNumber}+${source.sourceId}`,
													) >= 0
												}
												onChecked={(val) => {
													const sensorString = generateSensorString({
														infra: selected,
														param,
														source,
													});
													if (val) {
														checkSensor(sensorString);
													} else {
														uncheckSensor(sensorString);
													}
												}}
												label={source.sourceName}
											/>
										);
									})}
								</div>
							</div>
						);
					})}
				</div>
			);
		}
		return null;
	}, [selectedOutfall, selectedSensors]);

	useEffect(() => {
		handleGetPluviometers();
		handleGetOutfall();
	}, []);

	useEffect(() => {
		setParameters([]);
		setSelectedSensors([]);
		setDataModalDetail([]);
		setIsDataAvailable(false);
		handleGetOutfall();
		handleGetPluviometers();
		setSelectedOutfall(null);
		setSelectedPluviometer(null);
		renderSensors();
	}, [context.selectedLocation]);

	useEffect(() => {
		if (selectedSensors.length > 0) {
			// fetchAllCurrentSensors();
		} else {
			setIsDataAvailable(false);
			setGraphOptions({});
			setDataModalDetail([]);
			allReadings.current = [];
			allResumes.current = [];
		}
		renderSensors();
	}, [selectedSensors, selectedOutfall, selectedPluviometer]);

	useEffect(() => {
		setIsSpinnerReadingVisible(true);
		setTimeout(() => {
			allReadings.current = [];
			allResumes.current = [];
			fetchDataToGraph();
		}, 2000);
	}, [context.selectedDate]);

	useEffect(() => {
		if (chartRef2.current) {
			const invisibleSeries = chartRef2.current.series.filter((serie: any) =>
				invisibleParams.includes(parseInt(serie.userOptions.paramId)),
			);
			const visibleSeries = chartRef2.current.series.filter(
				(serie: any) => !invisibleParams.includes(parseInt(serie.userOptions.paramId)),
			);
			invisibleSeries.forEach((is: any) => {
				is.setVisible(false);
			});
			visibleSeries.forEach((vs: any) => {
				vs.setVisible(true);
			});
		}
	}, [invisibleParams, chartRef2.current]);

	return (
		<div
			style={{
				paddingTop: '80px',
				height: '100vh',
				backgroundColor: '#F7F9FC',
			}}
			className="flex mro-collapse"
		>
			<div style={{ width: '30%' }} className="px-4">
				<span style={{ fontSize: '20px', color: '#023E65', fontWeight: 600 }}>MRO Reports</span>
				<div
					className="mt-3 ml-2 mr-2"
					style={{ maxHeight: 'calc(100vh - 190px)', overflow: 'auto' }}
				>
					{/* <Collapse
						defaultActiveKey={['outfall', 'pluviometer']}
						items={[
							{
								key: 'outfall',
								label: (
									<Tooltip position="up-left" title="Outfall">
										<div className="flex">
											<div className="flex" style={{ width: '-webkit-fill-available' }}>
												<OutfallIconSvg />
												<span className="ml-2" style={{ color: '#04628C' }}>
													Outfalls
												</span>
											</div>
											<div style={{ width: '10px' }}></div>
										</div>
									</Tooltip>
								),
								children: (
									<div className="flex column">
										<Combobox
											value={selectedOutfall}
											options={
												outfallList
													? outfallList.map((of) => ({
															label: of.infraestructureName,
															value: of.infraestructureId,
														}))
													: []
											}
											placeholder="Select a Asset"
											onChange={(val): any => {
												setSelectedOutfall(val);
											}}
										/>
										{renderOutfalls()}
									</div>
								),
							},
							{
								key: 'pluviometer',
								label: (
									<Tooltip position="up-left" title="Rain Gauges">
										<div className="flex">
											<div className="flex" style={{ width: '-webkit-fill-available' }}>
												<PluviometerIconSvg />
												<span className="ml-2" style={{ color: '#04628C' }}>
													Rain Gauges
												</span>
											</div>
											<div style={{ width: '10px' }}></div>
										</div>
									</Tooltip>
								),
								children: (
									<div className="flex column">
										{pluviometerList && (
											<Combobox
												value={selectedPluviometer}
												options={pluviometerList.map((pv) => ({
													label: pv.infraestructureName,
													value: pv.infraestructureId,
												}))}
												placeholder="Select a Asset"
												onChange={(val): any => {
													setSelectedPluviometer(val);
												}}
											/>
										)}
										{renderPluviometers()}
									</div>
								),
							},
						]}
					/> */}
					<Accordion type="multiple" defaultValue={['outfall', 'pluviometer']}>
						<Accordion.Item value="outfall">
							<Accordion.Trigger text="Outfalls" CustomIconComponent={OutfallIconSvg} />
							<Accordion.Content>
								<div className="flex column">
									<Combobox size='small' items={outfallList
												? outfallList.map((of) => ({
														label: of.infraestructureName,
														value: of.infraestructureId,
													}))
												: []} 
										value={selectedOutfall} 
										onValueChange={(val): any => {
											setSelectedOutfall(val);
										}}>
										<ComboboxInput placeholder="Select a Asset" />
										<ComboboxContent>
											<ComboboxEmpty>No items found.</ComboboxEmpty>
											<ComboboxList>
											{(item) => (
												<ComboboxItem key={item.value} value={item}>
												{item.label}
												</ComboboxItem>
											)}
											</ComboboxList>
										</ComboboxContent>
									</Combobox>
									{renderOutfalls()}
								</div>
							</Accordion.Content>
						</Accordion.Item>
						<Accordion.Item value="pluviometer">
							<Accordion.Trigger text="Rain Gauges" CustomIconComponent={PluviometerIconSvg} />
							<Accordion.Content>
								<div className="flex column">
									{pluviometerList && (
										
										<Combobox size='small' items={pluviometerList.map((pv) => ({
												label: pv.infraestructureName,
												value: pv.infraestructureId,
											}))} 
											value={selectedPluviometer} 
											onValueChange={(val): any => {
												setSelectedPluviometer(val);
											}}>,
											<ComboboxInput placeholder="Select a Asset" />
											<ComboboxContent>
												<ComboboxEmpty>No items found.</ComboboxEmpty>
												<ComboboxList>
												{(item) => (
													<ComboboxItem key={item.value} value={item}>
													{item.label}
													</ComboboxItem>
												)}
												</ComboboxList>
											</ComboboxContent>
										</Combobox>
										
									
									)}
									{renderPluviometers()}
								</div>
							</Accordion.Content>
						</Accordion.Item>
					</Accordion>
				</div>
			</div>
			<div style={{ width: '70%' }} className="px-4">
				<Spin spinning={isSpinnerReadingVisible}>
					<div
						style={{
							width: '100%',
							height: '100%',
							zIndex: '9',
						}}
					>
						<div className="flex column">
							<div
								className="grid p-2"
								style={{
									gridTemplateColumns: '1fr',
									backgroundColor: 'white',
									borderRadius: '8px',
								}}
							>
								<div className="self-center" style={{ height: 'calc(50vh - 55px)' }}>
									<div
										style={{
											display: isDataAvailable ? 'flex' : 'none',
											flexDirection: 'column',
										}}
									>
										<div
											className="flex items-center"
											style={{
												height: '30px',
											}}
										>
											<span className="mr-2">Dates selected</span>
											<div
												className="flex p-1"
												style={{
													backgroundColor: '#F7F9FC',
												}}
											>
												<span>{`${dayjs(context.selectedDate[0]).format(
													'YYYY/MM/DD - hh:mm a',
												)}`}</span>
												<div
													className="mx-2"
													style={{
														width: '1px',

														height: '100%',
														backgroundColor: '#E6E6E6',
													}}
												/>
												<span>{`${dayjs(context.selectedDate[1]).format(
													'YYYY/MM/DD - hh:mm a',
												)}`}</span>
											</div>
										</div>
										<div style={{ height: 'calc(50vh - 90px)' }}>
											<HighchartsReact
												highcharts={Highcharts}
												options={graphOptions}
												containerProps={{ style: { height: '100%' } }}
											/>
										</div>
									</div>
									<div style={{ display: !isDataAvailable ? 'flex' : 'none' }}>
										<NoDataTableSvg style={{ width: '100%', height: '100%' }} />
									</div>
								</div>
							</div>
							{/* <Divider /> */}
							<div
								className="flex column mt-2 p-2"
								style={{
									backgroundColor: 'white',
									borderRadius: '8px',
									height: 'calc(50vh - 90px)',
								}}
							>
								<div className="flex items-center gap-2 justify-between" style={{ height: '52px', marginBottom: '1rem' }}>
									<span
										className="mr-2"
										style={{
											color: '#023E65',
											fontWeight: 'bold',
											textTransform: 'uppercase',
										}}
									>
										Current readings
									</span>
									<Input
										placeholder="Search Parameters"
										size='small'
										leftIcon={<SearchOutlined />}
										onChange={(e) => {
											setQueryParams(e.target.value);
										}}
									/>
									<Button
										variant="primary"
										className="flex items-center self-center ml-2"
										iconName={'download'}
										onClick={() => {
											handleGetMroReport();
										}}
										disabled={selectedSensors.length <= 0}
										label='Download'
									/>
								</div>
								<div className="flex w-full justify-end mt-2 mb-1">
									<LegendTable data={legendInfo} fontSize="small" />
								</div>
								<div
									className="flex flex-col items-center content-center"
									style={{
										width: '100%',
										placeContent: 'center',
										height: 'calc(80vh - 142px)',
										overflow: 'auto',
									}}
								>
									{selectedSensors.length <= 0 ? (
										<div>
											<span style={{ color: '#22A2CA', fontSize: '16px' }}>
												Select one flow parameter to start
											</span>
										</div>
									) : (
										<DataGrid columns={columnsExpanded} data={paramsToShowInTable()} pagination={{enabled: false}} />
									)}
								</div>
							</div>
						</div>
					</div>
				</Spin>
			</div>
		</div>
	);
}
